import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AsyncPipe, LowerCasePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { IProfilePictureService } from '@nimbus-library/features/user-settings/services/contracts/i-profile-picture.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IContactPresence } from '@nimbus-library/features/shared/models/contacts/i-contact.model';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { MenuActions } from '@nimbus-library/features/menu/state/menu.actions';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { PresenceStatus } from '@nimbus-global/features/shared/models/enums/presence-status.enum';
import { presenceMap } from '@nimbus-global/features/ui/models/presence-map.model';
import { PresenceActivityTranslateMap } from '@nimbus-global/features/ui/models/presence-activity.mapper';
import { PresenceStatusTranslateMap } from '@nimbus-global/features/ui/models/presence-status.mapper';
import { PresenceLookupService } from '@nimbus-global/features/ui/services/presence-lookup.service';
import { IUserResponsibilityProfile } from '@nimbus-library/features/user-state/models/user-responsibility-profile.model';
import { ResponsibilityProfileService } from '@nimbus-library/features/user-state/services/responsibility-profile.service';
import { UserTaskStateService } from '@nimbus-library/features/user-state/services/user-task-state.service';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { AgentAssistantAcwViewModel, UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { AgentAssistantService } from '@nimbus-library/features/agent-assistant/services/agent-assistant.service';
import { AgentAssistantAcwService } from '@nimbus-library/features/agent-assistant/services/agent-assistant-acw.service';
import { AgentAssistantRonaService } from '@nimbus-library/features/agent-assistant/services/agent-assistant-rona.service';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { HeaderBarStatusIndicator } from './header-bar-status-indicator.enum';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-header-bar-presence',
  templateUrl: './header-bar-presence.component.html',
  styleUrl: './header-bar-presence.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedUIModule, OverlayModule, AsyncPipe, LowerCasePipe, NgClass, TranslateModule],
})
export class HeaderBarPresenceComponent implements OnInit {
  private readonly namePlaceholder = 'Anonymous';

  private readonly acwExtendTrigger$ = new BehaviorSubject<boolean>(false);
  private readonly acwStopTrigger$ = new BehaviorSubject<boolean>(false);
  private readonly ronaStopTrigger$ = new BehaviorSubject<boolean>(false);

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly UserInteractionOutcome = UserInteractionOutcome;
  public readonly manualPresenceLabelMap: Partial<Record<PresenceStatus, TranslationKeys>> = {
    [PresenceStatus.Available]: TranslationKeys.PresenceAvailable,
    [PresenceStatus.Offline]: TranslationKeys.PresenceAppearOffline,
  };

  public readonly panelPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  public readonly isPanelOpen$ = new BehaviorSubject<boolean>(false);

  public readonly acwExtendDisabled$ = this.acwExtendTrigger$.asObservable();
  public readonly acwStopDisabled$ = this.acwStopTrigger$.asObservable();
  public readonly ronaStopDisabled$ = this.ronaStopTrigger$.asObservable();

  public readonly canSignOut$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((clientType) => clientType === ClientType.Browser),
      )
    : of(true);

  public presence$: Observable<IContactPresence | null>;
  public presenceTooltip$: Observable<string>;
  public userNotReadyReasonHint$ = this.userTaskStateService.getNotReadyReasonHint$();
  public userCanGetTasks$: Observable<boolean | null>;
  public isManualPresenceEnabled$: Observable<boolean>;
  public isManualPresenceSaving$: Observable<boolean>;

  public profiles$: Observable<IUserResponsibilityProfile[]>;
  public activeProfile$: Observable<IUserResponsibilityProfile | null>;
  public isActivatingProfile$: Observable<boolean>;
  public hasMultipleResponsibilityProfiles$: Observable<boolean>;
  public statusIndicator$: Observable<HeaderBarStatusIndicator>;

  public baseDate$: Observable<moment.Moment>;
  public ronaStartedAt$: Observable<string | null>;
  public afterCallWorkState$: Observable<AgentAssistantAcwViewModel | undefined>;

  public profilePicture: string;
  public name: string;

  public get manualPresenceOptions(): readonly PresenceStatus[] {
    return this.userStateService.manualPresenceOptions;
  }

  public constructor(
    private readonly profilePictureService: IProfilePictureService,
    private readonly userAuthenticationService: IAuthenticationService,
    private readonly userStateService: UserHeaderStateService,
    private readonly responsibilityProfileService: ResponsibilityProfileService,
    private readonly userTaskStateService: UserTaskStateService,
    private readonly presenceLookupService: PresenceLookupService,
    private readonly agentAssistantService: AgentAssistantService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly agentAssistantAcwService: AgentAssistantAcwService,
    private readonly agentAssistantRonaService: AgentAssistantRonaService,
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    const { userId, tenantId, name } = this.userAuthenticationService.getUserInfo();
    this.presence$ = this.userStateService.presence$;
    this.presenceTooltip$ = this.presence$.pipe(
      map((presence) =>
        this.presenceLookupService.getPresenceTitle(
          presence?.activity,
          presence?.availability,
          PresenceActivityTranslateMap,
          PresenceStatusTranslateMap,
        ),
      ),
      distinctUntilChanged(isEqual),
    );
    this.userCanGetTasks$ = this.userStateService.userCanGetTasks$;
    this.isManualPresenceEnabled$ = this.userStateService.isManualPresenceEnabled$;
    this.isManualPresenceSaving$ = this.userStateService.isManualPresenceSaving$;
    this.profilePicture = this.profilePictureService.getUserProfilePicture(tenantId, userId);
    this.name = name || this.namePlaceholder;

    this.responsibilityProfileService.initialize();
    this.profiles$ = this.responsibilityProfileService.profiles$;
    this.activeProfile$ = this.responsibilityProfileService.activeProfile$;
    this.isActivatingProfile$ = this.responsibilityProfileService.isActivating$;
    this.hasMultipleResponsibilityProfiles$ = this.profiles$.pipe(map((profiles) => profiles.length > 1));

    this.baseDate$ = this.backgroundTaskFactory.getServerWatches();
    this.ronaStartedAt$ = this.userTaskStateService.getRonaStartedAt$();
    this.afterCallWorkState$ = this.agentAssistantService.getAfterCallWorkState$();

    this.ronaStartedAt$.pipe(untilDestroyed(this)).subscribe(() => this.ronaStopTrigger$.next(false));
    this.afterCallWorkState$.pipe(untilDestroyed(this)).subscribe(() => {
      this.acwExtendTrigger$.next(false);
      this.acwStopTrigger$.next(false);
    });

    this.statusIndicator$ = combineLatest([this.ronaStartedAt$, this.afterCallWorkState$, this.userTaskStateService.canGetTask$()]).pipe(
      map(([ronaStartedAt, afterCallWorkState, userCanGetTasks]) => {
        if (ronaStartedAt) {
          return HeaderBarStatusIndicator.Rona;
        }
        if (afterCallWorkState) {
          return HeaderBarStatusIndicator.Acw;
        }
        return userCanGetTasks ? HeaderBarStatusIndicator.Ready : HeaderBarStatusIndicator.NotReady;
      }),
    );
  }

  public extendAcw(state: AgentAssistantAcwViewModel): void {
    this.acwExtendTrigger$.next(true);
    const { tenantId, userId } = this.userAuthenticationService.getUserInfo();
    this.agentAssistantAcwService
      .extendAcw(
        state.unifiedConversationId,
        state.conversationId,
        state.userSessionId,
        state.initialModality,
        state.channel,
        tenantId,
        userId,
      )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  public stopAcw(state: AgentAssistantAcwViewModel): void {
    this.acwStopTrigger$.next(true);
    const { tenantId, userId } = this.userAuthenticationService.getUserInfo();
    this.agentAssistantAcwService
      .stopAcw(
        state.unifiedConversationId,
        state.conversationId,
        state.userSessionId,
        state.initialModality,
        state.channel,
        tenantId,
        userId,
      )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  public stopRona(): void {
    this.ronaStopTrigger$.next(true);
    const { userId } = this.userAuthenticationService.getUserInfo();
    this.agentAssistantRonaService.stopRona(userId).pipe(untilDestroyed(this)).subscribe();
  }

  public manualPresenceLabel(status: PresenceStatus): TranslationKeys {
    return this.manualPresenceLabelMap[status] || presenceMap[status];
  }

  public togglePanel(): void {
    this.isPanelOpen$.next(!this.isPanelOpen$.value);
  }

  public closePanel(): void {
    this.isPanelOpen$.next(false);
  }

  public selectResponsibilityProfile(profile: IUserResponsibilityProfile): void {
    this.closePanel();
    this.responsibilityProfileService.activateProfile(profile.id).pipe(untilDestroyed(this)).subscribe();
  }

  public selectManualPresence(status: PresenceStatus): void {
    this.closePanel();
    this.userStateService.setManualPresence(status).pipe(untilDestroyed(this)).subscribe();
  }

  public openPreferences(): void {
    this.closePanel();
    this.router.navigate(['/user-preferences'], { queryParamsHandling: 'preserve' });
  }

  public signOut(): void {
    this.closePanel();
    this.store.dispatch(new MenuActions.LogOut());
  }
}
